import {
  Search,
  Globe,
  ShieldCheck,
  Palette,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Target,
  FileText,
  Users,
  Store,
  Building2,
  GraduationCap,
  HeartHandshake,
  ShoppingBag,
  Boxes,
  MonitorSmartphone,
  Sparkles,
  Settings,
  AppWindow,
  Bell,
  CreditCard,
  Activity,
  BriefcaseBusiness,
  Database,
  Code2,
  PanelsTopLeft,
  Zap,
  PackageCheck,
  ShoppingCart,
  Layers,
  RefreshCw,
  Workflow,
  ServerCog,
  Gauge,
  Newspaper,
  Blocks,
  Laptop,
  Package,
  Network,
  LayoutDashboard,
  Braces,
  FilePenLine,
  Images,
  Send,
  Cloud,
  DatabaseZap,
  ListTodo,
  CodeXml,
  Handshake,
  Wrench,
  TriangleAlert,
  DatabaseBackup,
  Bug,
  Headphones,
  Factory,
  Landmark,
} from "lucide-react";

import type { ServicePage } from "@/types/service";

export const WebsiteDevelopments: ServicePage[] = [
  // Website Development
  {
    title: "Website Development",
    slug: "website-development",
    group: "Website Development",
    icon: Code2,

    seo: {
      title: "Website Development Services",
      description:
        "Develop responsive, secure, and SEO-friendly websites that strengthen your digital presence, engage users, increase conversions, and support long-term business growth.",
      keywords: [
        "Website Development",
        "Website Development Services",
        "Web Development Services",
        "Custom Website Development",
        "Professional Website Development",
        "Website Development Company",
      ],
    },

    hero: {
      title: "Website Development for High-Performance Digital Experiences",
      description:
        "Build fast, accessible, SEO-ready web platforms that communicate value and convert qualified demand.",
      highlights: [
        "Responsive Design",
        "Fast Performance",
        "SEO Optimized",
        "Secure Architecture",
      ],
    },

    whyChoose: {
      title: "Why Invest in Professional Website Development?",
      description:
        "A professionally developed website gives your business a reliable digital foundation for building credibility, communicating value, attracting customers, and supporting long-term online growth.",
      items: [
        {
          title: "Strong Digital Presence",
          description:
            "Create a professional online presence that clearly communicates your business, services, expertise, and brand value.",
          icon: Globe,
        },
        {
          title: "Responsive User Experience",
          description:
            "Deliver consistent and intuitive website experiences across desktops, tablets, and mobile devices.",
          icon: MonitorSmartphone,
        },
        {
          title: "Search Visibility",
          description:
            "Build websites with structured content, technical foundations, and SEO-ready architecture that support organic discoverability.",
          icon: Search,
        },
        {
          title: "Business Growth",
          description:
            "Turn your website into a digital business asset designed to generate enquiries, conversions, and customer engagement.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Website Development Services",
      description:
        "End-to-end website development covering business websites, custom web platforms, responsive interfaces, CMS integration, and performance optimization.",
      items: [
        {
          title: "Custom Website Development",
          description:
            "Build tailored websites around your brand, business objectives, content requirements, customer journeys, and functionality.",
          icon: Code2,
          features: [
            "Custom Architecture",
            "Responsive Development",
            "Business Functionality",
            "API Integration",
            "Scalable Components",
          ],
        },
        {
          title: "Corporate Website Development",
          description:
            "Create professional corporate websites that communicate company credibility, capabilities, services, and business value.",
          icon: Building2,
          features: [
            "Corporate Design",
            "Service Pages",
            "Company Profiles",
            "Lead Generation",
            "SEO Structure",
          ],
        },
        {
          title: "Web Application Development",
          description:
            "Develop interactive web platforms, portals, dashboards, and browser-based business applications.",
          icon: AppWindow,
          features: [
            "Custom Web Applications",
            "User Authentication",
            "Dashboards",
            "Business Workflows",
            "Backend Integration",
          ],
        },
        {
          title: "Website Redesign & Modernization",
          description:
            "Transform outdated websites with improved design, architecture, responsiveness, performance, and user experience.",
          icon: RefreshCw,
          features: [
            "UI/UX Redesign",
            "Responsive Modernization",
            "Performance Improvements",
            "SEO Improvements",
            "Technology Upgrades",
          ],
        },
      ],
    },

    features: {
      title: "Key Website Development Features",
      description:
        "Essential capabilities for building fast, secure, accessible, search-friendly, and business-ready websites.",
      items: [
        {
          title: "Responsive Design",
          description:
            "Create layouts and interfaces that adapt smoothly across desktops, tablets, smartphones, and different screen sizes.",
          icon: MonitorSmartphone,
        },
        {
          title: "SEO-Friendly Architecture",
          description:
            "Structure pages, metadata, content, navigation, and technical foundations to support search engine visibility.",
          icon: Search,
        },
        {
          title: "Performance Optimization",
          description:
            "Optimize frontend delivery, assets, application behaviour, and loading performance for faster website experiences.",
          icon: Gauge,
        },
        {
          title: "Secure Architecture",
          description:
            "Apply reliable development practices across application structure, data handling, forms, APIs, and integrations.",
          icon: ShieldCheck,
        },
        {
          title: "Content Management",
          description:
            "Provide suitable content management capabilities that allow teams to manage website information efficiently.",
          icon: FileText,
        },
        {
          title: "Analytics Integration",
          description:
            "Connect suitable analytics and measurement tools to understand website traffic, engagement, and conversion activity.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our Website Development Process",
      description:
        "A structured website development process focused on business objectives, information architecture, user experience, engineering quality, performance, and reliable deployment.",
      items: [
        {
          title: "Discovery & Requirement Analysis",
          description:
            "Understand your business, target audience, services, website goals, content requirements, functionality, and technical needs.",
        },
        {
          title: "Website Architecture & Planning",
          description:
            "Define sitemap, page hierarchy, user journeys, content structure, technical architecture, integrations, and development roadmap.",
        },
        {
          title: "UI/UX Design",
          description:
            "Design responsive interfaces and page experiences aligned with your brand, audience, content, and conversion objectives.",
        },
        {
          title: "Website Development",
          description:
            "Develop responsive pages, reusable components, business functionality, CMS capabilities, APIs, and required integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test responsiveness, functionality, forms, integrations, accessibility, performance, SEO foundations, and browser compatibility.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the website to the production environment and provide ongoing maintenance, improvements, updates, and technical support.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Professional Website Development",
      description:
        "Build a reliable digital platform that strengthens your brand, improves customer experiences, supports search visibility, and contributes to business growth.",
      items: [
        {
          title: "Professional Brand Presence",
          description:
            "Present your business through a polished digital experience that builds credibility and communicates your value clearly.",
          icon: Building2,
        },
        {
          title: "Improved User Experience",
          description:
            "Make information, services, navigation, and conversion paths easier for visitors to understand and use.",
          icon: Users,
        },
        {
          title: "Better Search Foundations",
          description:
            "Create structured website architecture and content foundations that support technical and on-page SEO efforts.",
          icon: Search,
        },
        {
          title: "Higher Conversion Potential",
          description:
            "Guide visitors toward enquiries, purchases, registrations, or other important business actions through clear user journeys.",
          icon: Target,
        },
        {
          title: "Faster Website Performance",
          description:
            "Deliver optimized pages and frontend experiences designed to reduce unnecessary loading delays.",
          icon: Gauge,
        },
        {
          title: "Scalable Digital Foundation",
          description:
            "Build website architecture that can support additional pages, functionality, integrations, and future business requirements.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Website Development Technologies",
      description:
        "Modern frontend technologies used to build responsive, scalable, maintainable, and high-performance websites.",
      technologies: ["React", "Next.js", "Vite", "Tailwind CSS"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine web engineering, UI/UX, SEO-ready architecture, performance optimization, and business-focused development to build websites that support real digital growth.",
      items: [
        {
          title: "Custom Website Development",
          description:
            "Build websites around your brand, services, audience, content, functionality, and business requirements.",
          icon: Code2,
        },
        {
          title: "Responsive UI/UX",
          description:
            "Create polished website experiences that remain usable and consistent across different screen sizes and devices.",
          icon: MonitorSmartphone,
        },
        {
          title: "SEO-Ready Development",
          description:
            "Structure website architecture, metadata, content, and technical foundations with search visibility in mind.",
          icon: Search,
        },
        {
          title: "Performance-Focused Engineering",
          description:
            "Optimize frontend architecture, components, assets, and application delivery for responsive website experiences.",
          icon: Gauge,
        },
        {
          title: "Scalable Architecture",
          description:
            "Build maintainable website foundations capable of supporting future pages, functionality, integrations, and growth.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your website with troubleshooting, updates, performance improvements, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Website Development Solutions for Different Industries",
      description:
        "Professional websites and web platforms tailored to different audiences, business models, content requirements, and digital objectives.",
      items: [
        {
          title: "Technology & SaaS",
          description:
            "Build product-focused websites that communicate software capabilities, use cases, integrations, pricing, and conversion paths.",
          icon: Laptop,
        },
        {
          title: "Real Estate",
          description:
            "Create property websites with listings, project information, enquiries, lead generation, and customer communication.",
          icon: Building2,
        },
        {
          title: "Healthcare",
          description:
            "Develop appropriate websites for services, appointments, information, communication, and digital healthcare experiences.",
          icon: HeartHandshake,
        },
        {
          title: "Education",
          description:
            "Build websites for institutions, courses, programs, admissions, learning information, and student engagement.",
          icon: GraduationCap,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Create commerce-focused web experiences for product discovery, customer engagement, purchasing, and brand growth.",
          icon: ShoppingBag,
        },
        {
          title: "Professional Services",
          description:
            "Develop credible business websites that communicate expertise, services, case studies, and lead-generation opportunities.",
          icon: BriefcaseBusiness,
        },
      ],
    },

    portfolio: {
      title: "Website Development Success Stories",
      description:
        "Web platforms focused on professional design, responsive experiences, performance, search-ready architecture, and business conversions.",
      items: [
        {
          title: "Corporate Technology Website",
          description:
            "Developed a responsive corporate website with structured service architecture, SEO-ready pages, lead-generation journeys, and optimized frontend performance.",
          image: "/project-image.webp",
        },
        {
          title: "Business Service Platform",
          description:
            "Built a professional service website with clear information architecture, conversion-focused pages, enquiry workflows, and responsive user experiences.",
          image: "/project-image.webp",
        },
        {
          title: "Custom Web Platform",
          description:
            "Created a scalable web platform with reusable components, dynamic content, backend integrations, and responsive application experiences.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Website Developers",
      description:
        "Partner with our website developers to design, build, optimize, deploy, and support responsive, secure, SEO-ready, and scalable web platforms.",
      cta: "Let's Build Your Website",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to turn their digital requirements into professional and high-performing website experiences.",
      items: [
        {
          name: "James Anderson",
          company: "NorthBridge Solutions",
          location: "United Kingdom",
          review:
            "Skand Tech Hub transformed our website into a much clearer and more professional digital platform. The new structure makes our services easier to understand and the overall experience feels significantly faster.",
        },
        {
          name: "Michael Turner",
          company: "Vertex Consulting",
          location: "United States",
          review:
            "The team handled our website architecture, responsive development, and performance improvements very well. We now have a platform that represents the business properly and is much easier to expand.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Website Development services.",
      items: [
        {
          question: "What website development services do you provide?",
          answer:
            "We provide website planning, UI/UX design, custom website development, responsive frontend development, web application development, CMS integration, API integration, performance optimization, deployment, and ongoing technical support.",
        },
        {
          question: "Which technologies do you use for website development?",
          answer:
            "Our website development stack can include React, Next.js, Vite, Tailwind CSS, backend APIs, content management systems, and other technologies selected according to project requirements.",
        },
        {
          question: "Will my website be mobile-friendly?",
          answer:
            "Yes. We develop responsive website experiences designed to adapt across relevant desktop, tablet, and mobile screen sizes.",
        },
        {
          question: "Do you build SEO-friendly websites?",
          answer:
            "Yes. We can structure websites with SEO-ready page architecture, metadata support, semantic content organization, responsive development, performance considerations, and other technical foundations required for ongoing SEO work.",
        },
        {
          question: "Can you redesign an existing website?",
          answer:
            "Yes. We can modernize existing websites with updated UI/UX, responsive layouts, improved architecture, performance optimization, technology upgrades, and additional functionality.",
        },
        {
          question: "Do you provide website maintenance and support?",
          answer:
            "Yes. We can provide troubleshooting, website updates, performance improvements, integration support, technical maintenance, and additional website development.",
        },
      ],
    },
  },
  // Wordpress Development
  {
    title: "WordPress Development",
    slug: "wordpress-development",
    group: "Website Development",
    icon: PanelsTopLeft,

    seo: {
      title: "WordPress Development Services",
      description:
        "Build professional, responsive, SEO-friendly, and easy-to-manage WordPress websites with custom themes, plugins, integrations, and scalable functionality.",
      keywords: [
        "WordPress Development",
        "WordPress Development Services",
        "WordPress Website Development",
        "Custom WordPress Development",
        "WordPress Developers",
        "WordPress Development Company",
      ],
    },

    hero: {
      title: "WordPress Development for Flexible & Scalable Websites",
      description:
        "Build professional WordPress websites that combine flexible content management, responsive experiences, SEO-ready architecture, and custom functionality.",
      highlights: [
        "Easy Content Management",
        "Custom Design",
        "SEO Friendly",
        "Scalable Functionality",
      ],
    },

    whyChoose: {
      title: "Why Choose WordPress Development?",
      description:
        "WordPress provides businesses with a flexible content management foundation for building professional websites that teams can manage, extend, and evolve as digital requirements grow.",
      items: [
        {
          title: "Easy Content Management",
          description:
            "Manage pages, posts, media, and website content through an accessible content management interface.",
          icon: FileText,
        },
        {
          title: "Flexible Customization",
          description:
            "Extend website design and functionality through custom themes, plugins, integrations, and tailored development.",
          icon: Settings,
        },
        {
          title: "SEO-Friendly Foundation",
          description:
            "Structure content, pages, metadata, URLs, and technical elements to support ongoing search optimization.",
          icon: Search,
        },
        {
          title: "Scalable Website Growth",
          description:
            "Expand your website with additional pages, content, functionality, integrations, and business capabilities over time.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our WordPress Development Services",
      description:
        "End-to-end WordPress development covering custom websites, themes, plugins, WooCommerce, integrations, optimization, migration, and ongoing support.",
      items: [
        {
          title: "Custom WordPress Website Development",
          description:
            "Build tailored WordPress websites around your brand, content, business requirements, and customer journeys.",
          icon: PanelsTopLeft,
          features: [
            "Custom Website Development",
            "Responsive Layouts",
            "Content Management",
            "Business Functionality",
            "API Integration",
          ],
        },
        {
          title: "Custom Theme Development",
          description:
            "Create custom WordPress themes designed around your visual identity, page requirements, content structure, and responsive experience.",
          icon: Palette,
          features: [
            "Custom Theme Design",
            "Responsive Development",
            "Reusable Components",
            "Custom Templates",
            "Brand Integration",
          ],
        },
        {
          title: "WordPress Plugin Development",
          description:
            "Develop or customize plugins to add business-specific functionality and integrations to your WordPress website.",
          icon: Blocks,
          features: [
            "Custom Plugins",
            "Plugin Customization",
            "API Integrations",
            "Business Workflows",
            "Third-Party Services",
          ],
        },
        {
          title: "WordPress Redesign & Migration",
          description:
            "Modernize existing WordPress websites or migrate suitable websites while preserving important content and improving the overall experience.",
          icon: RefreshCw,
          features: [
            "Website Redesign",
            "WordPress Migration",
            "Content Migration",
            "Performance Improvements",
            "Technology Updates",
          ],
        },
      ],
    },

    features: {
      title: "Key WordPress Website Features",
      description:
        "Essential capabilities for building manageable, responsive, secure, search-friendly, and business-ready WordPress websites.",
      items: [
        {
          title: "Content Management System",
          description:
            "Give authorized teams control over pages, posts, media, and website content through WordPress.",
          icon: FileText,
        },
        {
          title: "Responsive Design",
          description:
            "Create WordPress experiences that adapt smoothly across desktop, tablet, and mobile screen sizes.",
          icon: MonitorSmartphone,
        },
        {
          title: "SEO Configuration",
          description:
            "Support metadata, structured URLs, content organization, sitemaps, and other technical foundations required for SEO.",
          icon: Search,
        },
        {
          title: "Custom Functionality",
          description:
            "Extend WordPress with custom templates, plugins, forms, APIs, integrations, and business-specific workflows.",
          icon: Settings,
        },
        {
          title: "Performance Optimization",
          description:
            "Improve website delivery through suitable caching, asset optimization, image handling, and frontend performance practices.",
          icon: Gauge,
        },
        {
          title: "Security & Maintenance",
          description:
            "Maintain WordPress core, themes, plugins, access controls, backups, and other relevant website security practices.",
          icon: ShieldCheck,
        },
      ],
    },

    process: {
      title: "Our WordPress Development Process",
      description:
        "A structured WordPress development workflow focused on business requirements, content architecture, custom design, reliable development, testing, and maintainability.",
      items: [
        {
          title: "Discovery & Requirement Analysis",
          description:
            "Understand your business, target audience, content requirements, website objectives, functionality, integrations, and management needs.",
        },
        {
          title: "Content & Website Planning",
          description:
            "Define sitemap, page structure, content hierarchy, WordPress architecture, required plugins, integrations, and development roadmap.",
        },
        {
          title: "UI/UX & Theme Design",
          description:
            "Design responsive website interfaces and templates aligned with your brand, content, users, and conversion objectives.",
        },
        {
          title: "WordPress Development",
          description:
            "Develop themes, templates, content structures, plugins, forms, APIs, and required website functionality.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test responsiveness, content management, functionality, integrations, performance, SEO foundations, and browser compatibility.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the WordPress website and provide ongoing updates, maintenance, optimization, troubleshooting, and technical support.",
        },
      ],
    },

    benefits: {
      title: "Benefits of WordPress Development",
      description:
        "Create a professional website that gives your business control over content while providing the flexibility to expand functionality as requirements evolve.",
      items: [
        {
          title: "Simple Content Updates",
          description:
            "Allow authorized teams to manage website content without requiring development work for every routine update.",
          icon: FileText,
        },
        {
          title: "Flexible Website Development",
          description:
            "Customize themes, templates, plugins, integrations, and functionality according to business requirements.",
          icon: Settings,
        },
        {
          title: "Responsive Experiences",
          description:
            "Deliver accessible website experiences across desktop, tablet, and mobile devices.",
          icon: MonitorSmartphone,
        },
        {
          title: "SEO-Ready Structure",
          description:
            "Build content and technical foundations that support metadata, search crawling, structured pages, and ongoing SEO efforts.",
          icon: Search,
        },
        {
          title: "Extensible Functionality",
          description:
            "Add new pages, integrations, forms, commerce capabilities, and business functionality as your website evolves.",
          icon: Blocks,
        },
        {
          title: "Long-Term Website Growth",
          description:
            "Maintain and extend the website as your content, services, audience, and digital requirements increase.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "WordPress Development Technologies",
      description:
        "Technologies used to develop, customize, style, and extend professional WordPress websites.",
      technologies: ["WordPress", "PHP", "MySQL", "WooCommerce", "Elementor"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine WordPress development, custom UI/UX, responsive engineering, SEO-ready architecture, integrations, and ongoing technical support to build websites around real business requirements.",
      items: [
        {
          title: "Custom WordPress Development",
          description:
            "Build WordPress websites around your brand, content, functionality, audience, and business objectives.",
          icon: PanelsTopLeft,
        },
        {
          title: "Custom Theme & Plugin Development",
          description:
            "Create tailored themes, templates, plugins, and functionality instead of relying only on generic configurations.",
          icon: Blocks,
        },
        {
          title: "Responsive UI/UX",
          description:
            "Develop professional WordPress experiences that remain consistent and usable across relevant screen sizes.",
          icon: MonitorSmartphone,
        },
        {
          title: "SEO-Ready Development",
          description:
            "Structure pages, content, metadata support, URLs, and technical foundations with search optimization in mind.",
          icon: Search,
        },
        {
          title: "Performance & Security Focus",
          description:
            "Apply suitable optimization, maintenance, update, backup, and website security practices.",
          icon: ShieldCheck,
        },
        {
          title: "Ongoing WordPress Support",
          description:
            "Support your website with updates, troubleshooting, plugin maintenance, optimization, integrations, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "WordPress Solutions for Different Industries",
      description:
        "Flexible WordPress websites tailored to different content requirements, audiences, business models, and digital objectives.",
      items: [
        {
          title: "Corporate & Professional Services",
          description:
            "Build professional websites that communicate company capabilities, expertise, services, case studies, and enquiry opportunities.",
          icon: BriefcaseBusiness,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Create WordPress and WooCommerce experiences for product discovery, purchasing, customer accounts, and online sales.",
          icon: ShoppingBag,
        },
        {
          title: "Real Estate",
          description:
            "Develop property websites with project information, listings, enquiries, content management, and lead-generation functionality.",
          icon: Building2,
        },
        {
          title: "Education",
          description:
            "Build websites for institutions, programs, courses, admissions, content, news, and student information.",
          icon: GraduationCap,
        },
        {
          title: "Healthcare",
          description:
            "Develop appropriate WordPress websites for services, information, appointments, content, and customer communication.",
          icon: HeartHandshake,
        },
        {
          title: "Blogs & Content Platforms",
          description:
            "Create structured publishing platforms for articles, categories, authors, media, search, and ongoing content growth.",
          icon: Newspaper,
        },
      ],
    },

    portfolio: {
      title: "WordPress Development Success Stories",
      description:
        "WordPress websites focused on flexible content management, custom design, responsive experiences, performance, and scalable functionality.",
      items: [
        {
          title: "Corporate WordPress Website",
          description:
            "Developed a custom WordPress website with structured service pages, reusable templates, content management, responsive design, and lead-generation workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Content Publishing Platform",
          description:
            "Built a WordPress content platform with organized categories, custom templates, editorial workflows, search-friendly structure, and responsive experiences.",
          image: "/project-image.webp",
        },
        {
          title: "WooCommerce Business Website",
          description:
            "Created a WordPress and WooCommerce website connecting branded content with product discovery, customer journeys, payments, and order functionality.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire WordPress Developers",
      description:
        "Partner with our WordPress developers to design, build, customize, optimize, migrate, and maintain professional websites tailored to your business.",
      cta: "Let's Build Your WordPress Website",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build WordPress websites that are professional, manageable, and flexible enough to grow with their requirements.",
      items: [
        {
          name: "Robert Mitchell",
          company: "BrightStone Consulting",
          location: "United Kingdom",
          review:
            "Skand Tech Hub rebuilt our WordPress website around a much cleaner content structure. Our team can now manage updates easily without compromising the overall design.",
        },
        {
          name: "Daniel Brooks",
          company: "NorthPeak Group",
          location: "United States",
          review:
            "The custom WordPress work gave us far more flexibility than our previous template-based site. The website is responsive, easier to manage, and much better aligned with our business.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our WordPress Development services.",
      items: [
        {
          question: "What WordPress development services do you provide?",
          answer:
            "We provide custom WordPress website development, theme development, plugin customization and development, responsive development, WooCommerce integration, API integration, website redesign, migration, optimization, and ongoing maintenance.",
        },
        {
          question: "Can you build a completely custom WordPress website?",
          answer:
            "Yes. We can develop custom WordPress websites with tailored page structures, themes, templates, functionality, integrations, and content management workflows based on your requirements.",
        },
        {
          question: "Can you redesign my existing WordPress website?",
          answer:
            "Yes. We can redesign and modernize existing WordPress websites with improved UI/UX, responsive layouts, updated functionality, performance improvements, and technical enhancements.",
        },
        {
          question: "Can you develop custom WordPress plugins?",
          answer:
            "Yes. Where appropriate, we can develop or customize WordPress plugins to support business-specific functionality, integrations, and website workflows.",
        },
        {
          question: "Can you build an e-commerce website with WordPress?",
          answer:
            "Yes. WordPress can be combined with WooCommerce to build suitable e-commerce websites with product catalogs, customer accounts, carts, checkout, payments, orders, and related functionality.",
        },
        {
          question: "Do you provide WordPress maintenance?",
          answer:
            "Yes. We can provide WordPress core, theme and plugin updates, troubleshooting, backups, performance improvements, security-related maintenance, integrations, and additional development.",
        },
      ],
    },
  },
  // Ecommerce Development
  {
    title: "E-Commerce Website Development",
    slug: "e-commerce-website-development",
    group: "Website Development",
    icon: ShoppingCart,

    seo: {
      title: "E-Commerce Website Development Services",
      description:
        "Build secure, scalable, and conversion-focused e-commerce websites with seamless shopping experiences, product management, secure payments, and mobile-friendly design.",
      keywords: [
        "E-Commerce Website Development",
        "E-Commerce Web Development",
        "Online Store Development",
        "E-Commerce Development Services",
        "E-Commerce Website Developers",
        "E-Commerce Website Development Company",
      ],
    },

    hero: {
      title: "E-Commerce Website Development for Scalable Online Growth",
      description:
        "Create high-performing eCommerce websites with secure payments, intuitive shopping experiences, and scalable architecture for long-term business growth.",
      highlights: [
        "Secure Payments",
        "Product Management",
        "Mobile-Friendly",
        "Conversion Focused",
      ],
    },

    whyChoose: {
      title: "Why Invest in E-Commerce Website Development?",
      description:
        "A professionally developed e-commerce website gives your business a scalable digital storefront where customers can discover products, purchase securely, manage orders, and engage with your brand.",
      items: [
        {
          title: "Sell Products Online",
          description:
            "Create a complete digital storefront where customers can discover, evaluate, and purchase your products online.",
          icon: ShoppingCart,
        },
        {
          title: "Secure Online Payments",
          description:
            "Enable reliable checkout experiences with suitable payment gateways and secure transaction workflows.",
          icon: CreditCard,
        },
        {
          title: "Better Shopping Experience",
          description:
            "Create intuitive product discovery, navigation, cart, checkout, and account experiences across devices.",
          icon: ShoppingBag,
        },
        {
          title: "Scalable Online Growth",
          description:
            "Build a commerce foundation capable of supporting growing products, customers, orders, integrations, and business requirements.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our E-Commerce Website Development Services",
      description:
        "End-to-end e-commerce development covering custom storefronts, product catalogs, payments, platform integrations, checkout experiences, and ongoing optimization.",
      items: [
        {
          title: "Custom E-Commerce Development",
          description:
            "Build tailored online stores around your products, customers, brand, business model, and operational requirements.",
          icon: ShoppingCart,
          features: [
            "Custom Storefront",
            "Product Catalog",
            "Customer Accounts",
            "Cart & Checkout",
            "Order Management",
          ],
        },
        {
          title: "E-Commerce Platform Development",
          description:
            "Develop online stores using suitable commerce platforms based on your catalog, operational, and business requirements.",
          icon: Store,
          features: [
            "Shopify Development",
            "WooCommerce Development",
            "Magento Development",
            "Store Configuration",
            "Custom Integrations",
          ],
        },
        {
          title: "Payment & Checkout Integration",
          description:
            "Create streamlined purchasing workflows with suitable payment gateways, checkout functionality, discounts, and transaction management.",
          icon: CreditCard,
          features: [
            "Payment Gateway Integration",
            "Secure Checkout",
            "Coupons & Discounts",
            "Transaction Workflows",
            "Payment Status",
          ],
        },
        {
          title: "E-Commerce Website Modernization",
          description:
            "Improve existing online stores with redesigned shopping experiences, updated technology, integrations, and performance improvements.",
          icon: RefreshCw,
          features: [
            "Store Redesign",
            "Platform Migration",
            "Performance Optimization",
            "Checkout Improvements",
            "Feature Enhancement",
          ],
        },
      ],
    },

    features: {
      title: "Key E-Commerce Website Features",
      description:
        "Essential functionality for creating convenient, secure, manageable, and conversion-focused online shopping experiences.",
      items: [
        {
          title: "Product Management",
          description:
            "Manage products, categories, variants, pricing, images, descriptions, availability, and other catalog information.",
          icon: Package,
        },
        {
          title: "Search & Filters",
          description:
            "Help customers discover relevant products through structured categories, search, filtering, and sorting functionality.",
          icon: Search,
        },
        {
          title: "Shopping Cart & Checkout",
          description:
            "Provide clear cart and checkout journeys designed to simplify the purchasing process.",
          icon: ShoppingCart,
        },
        {
          title: "Secure Payments",
          description:
            "Integrate suitable payment gateways and transaction workflows according to your market and business requirements.",
          icon: CreditCard,
        },
        {
          title: "Order Management",
          description:
            "Manage customer orders, statuses, purchase information, fulfillment workflows, and relevant notifications.",
          icon: PackageCheck,
        },
        {
          title: "Customer Accounts",
          description:
            "Allow customers to manage profiles, addresses, orders, preferences, and relevant shopping information.",
          icon: Users,
        },
      ],
    },

    process: {
      title: "Our E-Commerce Website Development Process",
      description:
        "A structured commerce development process focused on customer journeys, product architecture, secure transactions, platform integrations, performance, and reliable releases.",
      items: [
        {
          title: "Business & Store Discovery",
          description:
            "Understand your products, customers, business model, inventory, payments, fulfillment, integrations, and online sales objectives.",
        },
        {
          title: "Commerce Architecture & Planning",
          description:
            "Define product structures, categories, customer journeys, cart and checkout workflows, platform architecture, and required integrations.",
        },
        {
          title: "E-Commerce UI/UX Design",
          description:
            "Design responsive storefront experiences covering product discovery, product details, carts, checkout, accounts, and orders.",
        },
        {
          title: "Store Development & Integration",
          description:
            "Develop the storefront, product functionality, customer accounts, payments, orders, APIs, and required commerce integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test shopping journeys, payments, orders, responsiveness, integrations, usability, security, and website performance.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the e-commerce website and provide ongoing maintenance, optimization, integrations, and additional functionality.",
        },
      ],
    },

    benefits: {
      title: "Benefits of E-Commerce Website Development",
      description:
        "Create a scalable online sales channel that makes purchasing convenient for customers while connecting digital commerce with your business operations.",
      items: [
        {
          title: "24/7 Online Storefront",
          description:
            "Give customers continuous digital access to browse products, review information, and place orders online.",
          icon: Store,
        },
        {
          title: "Wider Customer Reach",
          description:
            "Make your products accessible to customers beyond the limitations of a physical business location.",
          icon: Globe,
        },
        {
          title: "Improved Shopping Experience",
          description:
            "Create responsive product discovery, cart, checkout, account, and order experiences across devices.",
          icon: ShoppingBag,
        },
        {
          title: "Conversion Opportunities",
          description:
            "Create clear purchasing journeys, promotions, product discovery, and checkout experiences designed around online sales.",
          icon: Target,
        },
        {
          title: "Connected Commerce Operations",
          description:
            "Integrate storefront functionality with payments, inventory, shipping, analytics, and other suitable business systems.",
          icon: Boxes,
        },
        {
          title: "Scalable Business Growth",
          description:
            "Expand products, categories, functionality, integrations, and customer experiences as your online business grows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "E-Commerce Website Development Technologies",
      description:
        "Commerce platforms, frontend technologies, and payment systems used according to storefront and business requirements.",
      technologies: ["Shopify", "WooCommerce", "Magento", "Next.js", "Stripe"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine commerce development, responsive UI/UX, payment integrations, platform engineering, and business-focused development to create online stores built around customer and operational requirements.",
      items: [
        {
          title: "Commerce-Focused Development",
          description:
            "Build online stores around product discovery, customer journeys, purchasing, orders, and business operations.",
          icon: ShoppingCart,
        },
        {
          title: "Conversion-Focused UI/UX",
          description:
            "Create clear product, cart, and checkout experiences designed to reduce unnecessary friction in the buying journey.",
          icon: Target,
        },
        {
          title: "Secure Payment Integration",
          description:
            "Connect suitable payment gateways and transaction workflows according to your commerce requirements.",
          icon: ShieldCheck,
        },
        {
          title: "Platform Expertise",
          description:
            "Develop commerce experiences using suitable platforms such as Shopify, WooCommerce, Magento, or custom technologies.",
          icon: Store,
        },
        {
          title: "Scalable Architecture",
          description:
            "Build commerce foundations capable of supporting increasing products, customers, orders, integrations, and functionality.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your online store with troubleshooting, updates, integrations, optimization, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "E-Commerce Website Solutions for Different Industries",
      description:
        "Online stores tailored to different product catalogs, customer journeys, purchasing models, and fulfillment requirements.",
      items: [
        {
          title: "Fashion & Apparel",
          description:
            "Create online stores with product collections, variants, size options, promotions, customer accounts, and streamlined checkout.",
          icon: ShoppingBag,
        },
        {
          title: "Beauty & Cosmetics",
          description:
            "Build visually engaging commerce experiences for product collections, recommendations, promotions, and repeat purchasing.",
          icon: Sparkles,
        },
        {
          title: "Electronics",
          description:
            "Manage detailed product catalogs, specifications, variants, search, filters, availability, and customer purchasing journeys.",
          icon: Laptop,
        },
        {
          title: "Food & Grocery",
          description:
            "Develop online shopping experiences for product catalogs, carts, payments, delivery options, orders, and promotions.",
          icon: Store,
        },
        {
          title: "Home & Lifestyle",
          description:
            "Create structured commerce websites for broad catalogs, categories, product options, and delivery requirements.",
          icon: Package,
        },
        {
          title: "D2C Brands",
          description:
            "Build direct-to-consumer storefronts focused on brand experience, customer acquisition, conversion, and repeat purchases.",
          icon: TrendingUp,
        },
      ],
    },

    portfolio: {
      title: "E-Commerce Website Development Success Stories",
      description:
        "Commerce platforms focused on product discovery, secure purchasing, responsive experiences, and connected order operations.",
      items: [
        {
          title: "D2C E-Commerce Store",
          description:
            "Developed a responsive online storefront with product collections, customer accounts, secure payments, order management, and promotional workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Multi-Category Online Store",
          description:
            "Built an e-commerce platform with structured categories, product search, advanced filters, cart functionality, checkout, and order management.",
          image: "/project-image.webp",
        },
        {
          title: "Commerce Platform Modernization",
          description:
            "Modernized an existing online store with improved UI/UX, responsive shopping journeys, payment integrations, and performance optimization.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire E-Commerce Website Developers",
      description:
        "Partner with our e-commerce developers to build, integrate, optimize, migrate, and support secure and conversion-focused online stores.",
      cta: "Let's Build Your E-Commerce Website",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create e-commerce experiences that connect customer journeys with reliable online sales operations.",
      items: [
        {
          name: "Sophie Bennett",
          company: "UrbanNest Retail",
          location: "United Kingdom",
          review:
            "Skand Tech Hub helped us rebuild our online store around a much smoother shopping journey. Product discovery and checkout are easier for customers, while managing the store is simpler for our team.",
        },
        {
          name: "David Parker",
          company: "NorthLane Commerce",
          location: "United States",
          review:
            "The team handled our storefront, payment integration, and responsive development very well. The new platform gives us a much stronger foundation for growing our online sales.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our E-Commerce Website Development services.",
      items: [
        {
          question:
            "What e-commerce website development services do you provide?",
          answer:
            "We provide e-commerce planning, storefront development, product and catalog functionality, customer accounts, cart and checkout development, payment integration, order workflows, platform customization, migration, optimization, and ongoing support.",
        },
        {
          question: "Which platforms do you use for e-commerce development?",
          answer:
            "Depending on project requirements, we can work with technologies and platforms such as Shopify, WooCommerce, Magento, Next.js, Stripe, and other suitable commerce integrations.",
        },
        {
          question: "Can you integrate payment gateways into an online store?",
          answer:
            "Yes. We can integrate suitable payment gateways according to your market, platform, account configuration, and transaction requirements.",
        },
        {
          question: "Will the e-commerce website be mobile-friendly?",
          answer:
            "Yes. We develop responsive shopping experiences designed to work across relevant desktop, tablet, and mobile screen sizes.",
        },
        {
          question: "Can you redesign or migrate an existing online store?",
          answer:
            "Yes. We can modernize or migrate suitable existing e-commerce websites while improving UI/UX, technology, performance, integrations, and shopping workflows.",
        },
        {
          question: "Do you provide e-commerce website maintenance?",
          answer:
            "Yes. We can provide troubleshooting, platform updates, integration support, performance improvements, payment-related technical support, and additional commerce development.",
        },
      ],
    },
  },
  // CMS Development
  {
    title: "CMS Development",
    slug: "cms-development",
    group: "Website Development",
    icon: LayoutDashboard,

    seo: {
      title: "CMS Development Services",
      description:
        "Build flexible, secure, and scalable content management systems that simplify content publishing, streamline workflows, and give businesses greater control over their digital content.",
      keywords: [
        "CMS Development",
        "CMS Development Services",
        "Custom CMS Development",
        "Content Management System Development",
        "CMS Developers",
        "CMS Development Company",
      ],
    },

    hero: {
      title: "CMS Development for Flexible Content Management",
      description:
        "Build scalable content management systems that give your team control over website content, publishing workflows, users, and digital experiences.",
      highlights: [
        "Easy Content Management",
        "Custom Workflows",
        "Role-Based Access",
        "Scalable Architecture",
      ],
    },

    whyChoose: {
      title: "Why Invest in CMS Development?",
      description:
        "A well-designed content management system allows teams to manage digital content efficiently without depending on developers for routine publishing and website updates.",
      items: [
        {
          title: "Content Control",
          description:
            "Give authorized teams direct control over pages, articles, media, categories, and other website content.",
          icon: FileText,
        },
        {
          title: "Efficient Publishing",
          description:
            "Simplify content creation, editing, review, scheduling, and publishing through structured management workflows.",
          icon: Send,
        },
        {
          title: "Role-Based Management",
          description:
            "Define suitable permissions and content responsibilities for administrators, editors, authors, and other users.",
          icon: Users,
        },
        {
          title: "Scalable Content Operations",
          description:
            "Create a content foundation capable of supporting increasing pages, content types, users, languages, and digital channels.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our CMS Development Services",
      description:
        "End-to-end CMS development covering custom content management systems, headless CMS implementations, integrations, migrations, and ongoing optimization.",
      items: [
        {
          title: "Custom CMS Development",
          description:
            "Build tailored content management systems around your content structures, users, publishing workflows, and business requirements.",
          icon: LayoutDashboard,
          features: [
            "Custom Content Models",
            "Admin Dashboard",
            "Publishing Workflows",
            "Role-Based Access",
            "Media Management",
          ],
        },
        {
          title: "Headless CMS Development",
          description:
            "Separate content management from frontend presentation to distribute structured content across websites, applications, and other digital experiences.",
          icon: Network,
          features: [
            "Headless Architecture",
            "Content APIs",
            "Structured Content",
            "Multi-Channel Delivery",
            "Frontend Integration",
          ],
        },
        {
          title: "CMS Integration",
          description:
            "Connect content management systems with websites, applications, analytics platforms, CRM systems, and other business tools.",
          icon: Boxes,
          features: [
            "API Integration",
            "Frontend Integration",
            "CRM Integration",
            "Analytics Integration",
            "Third-Party Services",
          ],
        },
        {
          title: "CMS Migration & Modernization",
          description:
            "Move suitable existing content and workflows to a modern CMS while improving structure, usability, performance, and maintainability.",
          icon: RefreshCw,
          features: [
            "Content Migration",
            "CMS Migration",
            "Architecture Modernization",
            "Workflow Improvements",
            "Performance Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key CMS Features",
      description:
        "Core capabilities for managing structured content, publishing workflows, users, media, and digital experiences efficiently.",
      items: [
        {
          title: "Content Editor",
          description:
            "Create and manage structured website content through an accessible administrative interface.",
          icon: FilePenLine,
        },
        {
          title: "Media Management",
          description:
            "Organize and manage images, documents, videos, and other digital assets from a centralized content library.",
          icon: Images,
        },
        {
          title: "Role-Based Access",
          description:
            "Control administrative functionality and content permissions according to different user roles and responsibilities.",
          icon: ShieldCheck,
        },
        {
          title: "Publishing Workflows",
          description:
            "Support suitable draft, review, approval, scheduling, and publishing processes for content teams.",
          icon: Workflow,
        },
        {
          title: "SEO Management",
          description:
            "Manage relevant page metadata, URLs, content structure, and other SEO-related information through the CMS.",
          icon: Search,
        },
        {
          title: "Content APIs",
          description:
            "Expose structured content through suitable APIs for websites, mobile applications, and other digital channels.",
          icon: Braces,
        },
      ],
    },

    process: {
      title: "Our CMS Development Process",
      description:
        "A structured development process focused on understanding content operations, defining scalable content architecture, building management workflows, and delivering a maintainable CMS.",
      items: [
        {
          title: "Content & Workflow Discovery",
          description:
            "Understand your content types, publishing requirements, users, approval processes, existing systems, and digital channels.",
        },
        {
          title: "CMS Architecture Planning",
          description:
            "Define content models, relationships, user roles, permissions, publishing workflows, APIs, integrations, and technical architecture.",
        },
        {
          title: "Admin UX Design",
          description:
            "Design clear content management experiences that make common editing, publishing, and administrative tasks easier for teams.",
        },
        {
          title: "CMS Development & Integration",
          description:
            "Develop content models, administrative functionality, workflows, APIs, media management, and required business integrations.",
        },
        {
          title: "Testing & Content Validation",
          description:
            "Test content creation, permissions, publishing workflows, integrations, APIs, responsiveness, and content delivery.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the CMS and provide ongoing maintenance, updates, integrations, workflow improvements, and technical support.",
        },
      ],
    },

    benefits: {
      title: "Benefits of CMS Development",
      description:
        "Give your organization greater control over digital content while improving publishing efficiency, consistency, collaboration, and scalability.",
      items: [
        {
          title: "Faster Content Updates",
          description:
            "Allow authorized teams to create and update content without requiring development changes for routine publishing.",
          icon: Zap,
        },
        {
          title: "Centralized Content Management",
          description:
            "Manage pages, media, structured content, and publishing information from a centralized administrative system.",
          icon: LayoutDashboard,
        },
        {
          title: "Improved Team Collaboration",
          description:
            "Support content responsibilities through appropriate roles, permissions, review processes, and publishing workflows.",
          icon: Users,
        },
        {
          title: "Consistent Digital Content",
          description:
            "Use structured content models and reusable fields to maintain greater consistency across pages and digital experiences.",
          icon: Layers,
        },
        {
          title: "Multi-Channel Content Delivery",
          description:
            "Use suitable headless architecture to distribute structured content across websites, applications, and other digital channels.",
          icon: Network,
        },
        {
          title: "Scalable Content Growth",
          description:
            "Extend content models, workflows, users, integrations, and digital experiences as organizational requirements grow.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "CMS Development Technologies",
      description:
        "Content management platforms and web technologies selected according to content architecture, publishing workflows, and frontend requirements.",
      technologies: ["WordPress", "Strapi", "Contentful", "Sanity", "Next.js"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine CMS architecture, web development, content workflows, API integrations, and frontend engineering to create manageable digital platforms around real business requirements.",
      items: [
        {
          title: "Custom CMS Architecture",
          description:
            "Design content structures, relationships, workflows, and administrative functionality around your organization's requirements.",
          icon: LayoutDashboard,
        },
        {
          title: "Headless CMS Expertise",
          description:
            "Build API-driven content foundations for flexible delivery across websites, applications, and other digital experiences.",
          icon: Network,
        },
        {
          title: "User-Friendly Administration",
          description:
            "Create practical management experiences that make routine content operations easier for authorized teams.",
          icon: Users,
        },
        {
          title: "Frontend & API Integration",
          description:
            "Connect CMS platforms with modern frontend applications, external APIs, analytics, CRM platforms, and business systems.",
          icon: Boxes,
        },
        {
          title: "Scalable Content Architecture",
          description:
            "Structure content models and systems to support growing content, users, channels, and functionality.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your CMS with troubleshooting, upgrades, integrations, migrations, optimization, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "CMS Solutions for Different Industries",
      description:
        "Content management systems tailored to different publishing requirements, organizational workflows, audiences, and digital channels.",
      items: [
        {
          title: "Media & Publishing",
          description:
            "Manage articles, categories, authors, media, editorial workflows, scheduled publishing, and high-volume content operations.",
          icon: Newspaper,
        },
        {
          title: "Corporate & Enterprise",
          description:
            "Create structured management systems for corporate pages, services, resources, news, teams, and multi-department content.",
          icon: Building2,
        },
        {
          title: "Education",
          description:
            "Manage institutional information, programs, courses, admissions content, resources, news, and educational materials.",
          icon: GraduationCap,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Manage editorial, promotional, category, campaign, and supporting commerce content across digital experiences.",
          icon: ShoppingBag,
        },
        {
          title: "Healthcare",
          description:
            "Create appropriate content management workflows for services, resources, information, locations, and digital communication.",
          icon: HeartHandshake,
        },
        {
          title: "Technology & SaaS",
          description:
            "Manage product pages, documentation, resources, use cases, integrations, blogs, and marketing content efficiently.",
          icon: Laptop,
        },
      ],
    },

    portfolio: {
      title: "CMS Development Success Stories",
      description:
        "Content platforms focused on structured publishing, efficient management workflows, flexible delivery, and scalable content architecture.",
      items: [
        {
          title: "Headless Content Platform",
          description:
            "Developed a structured CMS with reusable content models, editorial workflows, APIs, and frontend integration for multi-page digital experiences.",
          image: "/project-image.webp",
        },
        {
          title: "Corporate Content Management System",
          description:
            "Built a centralized CMS that allowed internal teams to manage service pages, resources, news, media, and website content through controlled workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Publishing Management Platform",
          description:
            "Created a content platform with articles, authors, categories, media management, editorial permissions, and structured publishing workflows.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire CMS Developers",
      description:
        "Partner with our CMS developers to design, build, integrate, migrate, and maintain flexible content management systems around your publishing requirements.",
      cta: "Let's Build Your CMS",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create content management systems that make digital publishing more structured and manageable.",
      items: [
        {
          name: "Jonathan Reed",
          company: "MediaCore Group",
          location: "United Kingdom",
          review:
            "Our previous publishing process involved too many manual steps. Skand Tech Hub created a much cleaner CMS workflow that gives our content team better control without relying on developers for routine updates.",
        },
        {
          name: "Kevin Morgan",
          company: "NexaDigital",
          location: "United States",
          review:
            "The headless CMS architecture has made it much easier for us to manage structured content and use it across different parts of our digital platform.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our CMS Development services.",
      items: [
        {
          question: "What is CMS development?",
          answer:
            "CMS development involves building or configuring a content management system that allows authorized users to create, edit, organize, manage, and publish digital content through an administrative interface.",
        },
        {
          question: "Can you build a custom CMS?",
          answer:
            "Yes. We can build suitable custom content management systems around specific content types, user roles, publishing workflows, integrations, and business requirements.",
        },
        {
          question: "What is a headless CMS?",
          answer:
            "A headless CMS separates content management from frontend presentation. Structured content is typically delivered through APIs so it can be used across websites, applications, and other digital experiences.",
        },
        {
          question: "Which CMS platforms do you work with?",
          answer:
            "Depending on project requirements, the technology stack can include WordPress, Strapi, Contentful, Sanity, Next.js, and other suitable content management technologies.",
        },
        {
          question: "Can you migrate content from our existing CMS?",
          answer:
            "Yes. Where technically suitable, we can migrate existing content into a new CMS while mapping content structures, media, metadata, URLs, and other relevant information.",
        },
        {
          question: "Do you provide CMS maintenance and support?",
          answer:
            "Yes. We can provide troubleshooting, upgrades, integrations, content architecture improvements, performance optimization, migration support, and additional CMS development.",
        },
      ],
    },
  },
  // Laravel Development
  {
    title: "Laravel Development",
    slug: "laravel-development",
    group: "Website Development",
    icon: DatabaseZap,

    seo: {
      title: "Laravel Development Services",
      description:
        "Build secure, scalable, and maintainable Laravel applications, portals, dashboards, and APIs with clean backend architecture tailored to your business requirements.",
      keywords: [
        "Laravel Development",
        "Laravel Development Services",
        "Laravel Web Development",
        "Laravel Application Development",
        "Laravel Developers",
        "Laravel Development Company",
      ],
    },

    hero: {
      title: "Laravel Development for Secure & Scalable Web Platforms",
      description:
        "Use Laravel to move from operational requirements to secure web platforms with clean architecture.",
      highlights: [
        "Modular Backend Services",
        "Admin Panels & Dashboards",
        "Queue & Notification Systems",
        "Scalable Architecture",
      ],
    },

    whyChoose: {
      title: "Why Choose Laravel Development?",
      description:
        "Laravel provides a structured PHP framework for building maintainable web applications, business portals, APIs, dashboards, and backend systems.",
      items: [
        {
          title: "Structured Architecture",
          description:
            "Build organized application foundations with clear separation between business logic, data, APIs, and user-facing functionality.",
          icon: Layers,
        },
        {
          title: "Rapid Development",
          description:
            "Use Laravel's development ecosystem and reusable framework capabilities to build business functionality efficiently.",
          icon: Zap,
        },
        {
          title: "Secure Applications",
          description:
            "Implement authentication, authorization, validation, and appropriate application security practices within structured Laravel systems.",
          icon: ShieldCheck,
        },
        {
          title: "Scalable Backend Systems",
          description:
            "Create backend architecture capable of supporting growing users, workflows, integrations, data, and application functionality.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Laravel Development Services",
      description:
        "End-to-end Laravel development covering custom applications, APIs, business portals, dashboards, integrations, modernization, and ongoing support.",
      items: [
        {
          title: "Custom Laravel Development",
          description:
            "Build tailored Laravel applications around your business processes, users, data, integrations, and operational requirements.",
          icon: DatabaseZap,
          features: [
            "Custom Applications",
            "Business Logic",
            "Role-Based Access",
            "Database Development",
            "Scalable Architecture",
          ],
        },
        {
          title: "Laravel API Development",
          description:
            "Develop structured APIs for mobile applications, web platforms, external services, and business system integrations.",
          icon: Braces,
          features: [
            "REST APIs",
            "Authentication",
            "API Validation",
            "Third-Party Integration",
            "Mobile App APIs",
          ],
        },
        {
          title: "Admin Panel & Dashboard Development",
          description:
            "Create centralized administrative systems for managing users, operations, content, reporting, and business workflows.",
          icon: LayoutDashboard,
          features: [
            "Admin Dashboards",
            "User Management",
            "Role Management",
            "Reports",
            "Operational Workflows",
          ],
        },
        {
          title: "Laravel Modernization & Migration",
          description:
            "Improve suitable existing PHP and Laravel applications with updated architecture, dependencies, functionality, and integrations.",
          icon: RefreshCw,
          features: [
            "Laravel Upgrades",
            "PHP Modernization",
            "Architecture Improvements",
            "Feature Enhancement",
            "Performance Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key Laravel Application Features",
      description:
        "Core capabilities for building structured, secure, integrated, and maintainable business applications with Laravel.",
      items: [
        {
          title: "Authentication & Authorization",
          description:
            "Implement secure user authentication, roles, permissions, account workflows, and controlled application access.",
          icon: ShieldCheck,
        },
        {
          title: "API Development",
          description:
            "Create structured backend APIs for frontend applications, mobile apps, integrations, and external systems.",
          icon: Braces,
        },
        {
          title: "Queue Processing",
          description:
            "Handle suitable background operations such as emails, notifications, imports, exports, and other asynchronous tasks.",
          icon: ListTodo,
        },
        {
          title: "Notification Systems",
          description:
            "Build application workflows for suitable email, database, push, or other notification channels.",
          icon: Bell,
        },
        {
          title: "Database Management",
          description:
            "Structure application data, relationships, queries, migrations, and business information using suitable database architecture.",
          icon: Database,
        },
        {
          title: "Reporting & Dashboards",
          description:
            "Provide administrators and business users with relevant operational information, reports, metrics, and management views.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our Laravel Development Process",
      description:
        "A structured Laravel development process focused on business requirements, backend architecture, reliable engineering, integrations, testing, and maintainability.",
      items: [
        {
          title: "Requirement Analysis",
          description:
            "Understand your business workflows, users, data, application requirements, integrations, and technical objectives.",
        },
        {
          title: "Architecture & Database Planning",
          description:
            "Define application modules, database structure, APIs, authentication, permissions, integrations, queues, and technical architecture.",
        },
        {
          title: "UI/UX & Workflow Design",
          description:
            "Design user journeys, administrative workflows, dashboards, forms, and application interfaces around operational requirements.",
        },
        {
          title: "Laravel Development",
          description:
            "Develop application modules, business logic, APIs, database functionality, authentication, queues, notifications, and integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test workflows, APIs, permissions, integrations, database operations, security, performance, and application behaviour.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the Laravel application and provide ongoing maintenance, upgrades, troubleshooting, optimization, and additional development.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Laravel Development",
      description:
        "Build structured PHP applications that support complex business workflows while remaining maintainable, extensible, and integration-ready.",
      items: [
        {
          title: "Maintainable Architecture",
          description:
            "Organize application functionality through structured modules, services, models, controllers, and reusable backend logic.",
          icon: Layers,
        },
        {
          title: "Faster Development",
          description:
            "Use Laravel's framework capabilities and ecosystem to implement common application requirements efficiently.",
          icon: Zap,
        },
        {
          title: "Secure Access Control",
          description:
            "Implement authentication, permissions, validation, and controlled access according to application requirements.",
          icon: ShieldCheck,
        },
        {
          title: "Powerful Integrations",
          description:
            "Connect Laravel applications with payment gateways, external APIs, mobile apps, communication services, and business systems.",
          icon: Boxes,
        },
        {
          title: "Background Processing",
          description:
            "Move suitable time-consuming operations into queues to support responsive application workflows.",
          icon: ListTodo,
        },
        {
          title: "Scalable Product Growth",
          description:
            "Extend application modules, APIs, workflows, integrations, and functionality as business requirements evolve.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Laravel Development Technologies",
      description:
        "Backend technologies used to build structured Laravel applications, APIs, databases, queues, and business platforms.",
      technologies: ["Laravel", "PHP", "MySQL", "Horizon"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine Laravel engineering, database architecture, API development, business workflows, and frontend integration to build practical web platforms around operational requirements.",
      items: [
        {
          title: "Laravel Development Expertise",
          description:
            "Build structured Laravel applications using maintainable architecture and modern PHP development practices.",
          icon: DatabaseZap,
        },
        {
          title: "Custom Business Logic",
          description:
            "Develop application modules and workflows around your actual operational and business requirements.",
          icon: Workflow,
        },
        {
          title: "API & Integration Expertise",
          description:
            "Connect Laravel applications with mobile apps, frontend platforms, payment systems, and third-party services.",
          icon: Braces,
        },
        {
          title: "Database Architecture",
          description:
            "Design structured database models and relationships around application data and business workflows.",
          icon: Database,
        },
        {
          title: "Scalable Backend Development",
          description:
            "Build backend foundations capable of supporting additional users, modules, integrations, and application functionality.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support Laravel applications with troubleshooting, upgrades, integrations, performance improvements, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Laravel Solutions for Different Industries",
      description:
        "Laravel applications tailored to different operational workflows, customer experiences, data requirements, and business systems.",
      items: [
        {
          title: "Enterprise & Business",
          description:
            "Build portals, dashboards, workflow systems, internal applications, and operational platforms.",
          icon: Building2,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Develop commerce backends, order workflows, inventory integrations, customer systems, and administrative platforms.",
          icon: ShoppingCart,
        },
        {
          title: "Healthcare",
          description:
            "Create appropriate applications for operational workflows, service management, appointments, communication, and administrative processes.",
          icon: HeartHandshake,
        },
        {
          title: "Education",
          description:
            "Build systems for students, courses, administration, assessments, communication, and institutional workflows.",
          icon: GraduationCap,
        },
        {
          title: "Real Estate",
          description:
            "Develop property management, lead workflows, customer portals, dashboards, and operational applications.",
          icon: Building2,
        },
        {
          title: "SaaS Platforms",
          description:
            "Create subscription-based web applications with user management, business workflows, APIs, dashboards, and scalable backend functionality.",
          icon: Cloud,
        },
      ],
    },

    portfolio: {
      title: "Laravel Development Success Stories",
      description:
        "Laravel platforms focused on structured backend architecture, business workflows, APIs, dashboards, and scalable application functionality.",
      items: [
        {
          title: "Enterprise Operations Portal",
          description:
            "Developed a Laravel platform with role-based access, operational workflows, dashboards, reports, notifications, and centralized business data.",
          image: "/project-image.webp",
        },
        {
          title: "Laravel API Platform",
          description:
            "Built structured backend APIs supporting mobile and web applications with authentication, business logic, database operations, and third-party integrations.",
          image: "/project-image.webp",
        },
        {
          title: "Business Management Dashboard",
          description:
            "Created an administrative platform for managing users, workflows, transactions, reporting, and operational information.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Laravel Developers",
      description:
        "Partner with our Laravel developers to build, integrate, modernize, optimize, and maintain secure web applications, APIs, portals, and business platforms.",
      cta: "Let's Build Your Laravel Application",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to build Laravel platforms that simplify complex backend and operational requirements.",
      items: [
        {
          name: "Richard Evans",
          company: "CoreBridge Systems",
          location: "United Kingdom",
          review:
            "Skand Tech Hub rebuilt our backend workflows into a much cleaner Laravel architecture. The admin system is easier to manage and new functionality is now far simpler to introduce.",
        },
        {
          name: "Anthony Miller",
          company: "NexaOperations",
          location: "United States",
          review:
            "The team handled our APIs, database workflows, queues, and dashboard requirements very well. The Laravel platform has given us a much stronger technical foundation.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Laravel Development services.",
      items: [
        {
          question: "What Laravel development services do you provide?",
          answer:
            "We provide custom Laravel application development, API development, admin panels, dashboards, database development, integrations, modernization, upgrades, testing, deployment, and ongoing technical support.",
        },
        {
          question: "Can Laravel be used for custom business applications?",
          answer:
            "Yes. Laravel can be used to build custom web applications, portals, dashboards, APIs, internal systems, and other business platforms around specific workflows and requirements.",
        },
        {
          question: "Can you build APIs with Laravel?",
          answer:
            "Yes. We can build Laravel APIs for web applications, mobile applications, third-party integrations, and other digital platforms.",
        },
        {
          question: "Can you upgrade an existing Laravel application?",
          answer:
            "Yes. We can assess suitable existing Laravel applications and perform framework upgrades, dependency updates, architecture improvements, feature development, and performance optimization.",
        },
        {
          question: "Can Laravel integrate with third-party systems?",
          answer:
            "Yes. Laravel applications can integrate with suitable payment gateways, communication platforms, external APIs, CRM systems, mobile applications, and other business services.",
        },
        {
          question: "Do you provide Laravel maintenance and support?",
          answer:
            "Yes. We can provide troubleshooting, framework upgrades, dependency maintenance, performance optimization, integration support, security-related improvements, and additional Laravel development.",
        },
      ],
    },
  },
  // Php Development
  {
    title: "PHP Development",
    slug: "php-development",
    group: "Website Development",
    icon: CodeXml,

    seo: {
      title: "PHP Development Services",
      description:
        "Build secure, scalable, and reliable PHP web applications, business platforms, APIs, portals, and backend systems tailored to your operational requirements.",
      keywords: [
        "PHP Development",
        "PHP Development Services",
        "PHP Web Development",
        "Custom PHP Development",
        "PHP Developers",
        "PHP Development Company",
      ],
    },

    hero: {
      title: "PHP Development for Reliable Web Applications",
      description:
        "Build custom PHP applications with structured backend architecture, secure business logic, database integration, and scalable functionality.",
      highlights: [
        "Custom PHP Development",
        "Backend Architecture",
        "API Integration",
        "Scalable Applications",
      ],
    },

    whyChoose: {
      title: "Why Choose PHP Development?",
      description:
        "PHP provides a flexible backend foundation for developing custom websites, web applications, business systems, APIs, and content-driven platforms.",
      items: [
        {
          title: "Flexible Development",
          description:
            "Build custom backend functionality around specific business processes, application requirements, and digital experiences.",
          icon: CodeXml,
        },
        {
          title: "Database-Driven Applications",
          description:
            "Create structured web applications that manage users, transactions, content, workflows, and business information.",
          icon: Database,
        },
        {
          title: "API & Integration Support",
          description:
            "Connect PHP applications with frontend platforms, mobile apps, payment systems, and suitable third-party services.",
          icon: Boxes,
        },
        {
          title: "Scalable Application Growth",
          description:
            "Extend application modules, functionality, integrations, and backend workflows as business requirements evolve.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our PHP Development Services",
      description:
        "End-to-end PHP development covering custom web applications, backend systems, APIs, integrations, modernization, and ongoing technical support.",
      items: [
        {
          title: "Custom PHP Web Development",
          description:
            "Develop tailored PHP websites and web applications around your business workflows, users, data, and functionality.",
          icon: CodeXml,
          features: [
            "Custom Web Applications",
            "Business Logic",
            "Database Integration",
            "User Management",
            "Custom Functionality",
          ],
        },
        {
          title: "PHP Backend Development",
          description:
            "Build backend systems that manage application data, authentication, business logic, workflows, and frontend communication.",
          icon: ServerCog,
          features: [
            "Backend Architecture",
            "Authentication",
            "Business Workflows",
            "Database Operations",
            "Admin Functionality",
          ],
        },
        {
          title: "PHP API Development",
          description:
            "Develop APIs for web applications, mobile apps, external services, and business system integrations.",
          icon: Braces,
          features: [
            "REST APIs",
            "API Authentication",
            "Data Integration",
            "Third-Party APIs",
            "Mobile App APIs",
          ],
        },
        {
          title: "PHP Modernization & Migration",
          description:
            "Improve suitable legacy PHP applications with updated code, architecture, dependencies, integrations, and functionality.",
          icon: RefreshCw,
          features: [
            "Legacy Modernization",
            "PHP Upgrades",
            "Code Refactoring",
            "Feature Enhancement",
            "Performance Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key PHP Application Features",
      description:
        "Core capabilities for developing secure, data-driven, integrated, and maintainable PHP applications.",
      items: [
        {
          title: "User Authentication",
          description:
            "Implement account registration, login, permissions, sessions, and controlled access according to application requirements.",
          icon: ShieldCheck,
        },
        {
          title: "Database Integration",
          description:
            "Manage structured application information, relationships, transactions, and business data through suitable databases.",
          icon: Database,
        },
        {
          title: "API Development",
          description:
            "Create backend APIs for frontend applications, mobile products, external systems, and integration workflows.",
          icon: Braces,
        },
        {
          title: "Admin Dashboards",
          description:
            "Provide administrative interfaces for managing users, content, transactions, reports, and operational workflows.",
          icon: LayoutDashboard,
        },
        {
          title: "Third-Party Integrations",
          description:
            "Connect applications with payment gateways, communication services, external APIs, analytics, and business tools.",
          icon: Boxes,
        },
        {
          title: "Reporting & Analytics",
          description:
            "Create suitable reports, dashboards, and operational views around relevant application and business data.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our PHP Development Process",
      description:
        "A structured PHP development workflow covering requirements, architecture, user experiences, backend engineering, testing, deployment, and ongoing support.",
      items: [
        {
          title: "Requirement Analysis",
          description:
            "Understand your users, workflows, business requirements, existing systems, data, integrations, and technical objectives.",
        },
        {
          title: "Architecture & Database Planning",
          description:
            "Define application modules, database structures, APIs, authentication, permissions, integrations, and backend architecture.",
        },
        {
          title: "UI/UX & Workflow Design",
          description:
            "Design application interfaces, user journeys, forms, dashboards, and administrative workflows around business requirements.",
        },
        {
          title: "PHP Development",
          description:
            "Develop backend functionality, database operations, APIs, authentication, business logic, and required integrations.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test application workflows, APIs, database operations, permissions, integrations, security, compatibility, and performance.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the PHP application and provide ongoing troubleshooting, upgrades, optimization, maintenance, and additional development.",
        },
      ],
    },

    benefits: {
      title: "Benefits of PHP Development",
      description:
        "Create flexible web applications and backend systems that support custom business workflows, integrations, and long-term digital requirements.",
      items: [
        {
          title: "Custom Business Functionality",
          description:
            "Develop backend workflows and application functionality specifically around your operational requirements.",
          icon: Workflow,
        },
        {
          title: "Flexible Architecture",
          description:
            "Structure PHP applications according to required modules, databases, integrations, and frontend experiences.",
          icon: Layers,
        },
        {
          title: "Database Connectivity",
          description:
            "Build data-driven applications for managing users, content, transactions, operations, and other business information.",
          icon: Database,
        },
        {
          title: "Integration Capabilities",
          description:
            "Connect PHP applications with APIs, payment systems, communication tools, analytics, and external business services.",
          icon: Boxes,
        },
        {
          title: "Maintainable Applications",
          description:
            "Organize application code and backend functionality to make future maintenance and development more manageable.",
          icon: Settings,
        },
        {
          title: "Scalable Product Growth",
          description:
            "Extend application functionality, integrations, workflows, and data capabilities as business requirements increase.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "PHP Development Technologies",
      description:
        "Backend and database technologies used to build reliable PHP websites, web applications, APIs, and business systems.",
      technologies: ["PHP", "Laravel", "MySQL", "REST API", "Composer"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine PHP backend engineering, database development, API integrations, application architecture, and business-focused development to create reliable web platforms.",
      items: [
        {
          title: "Custom PHP Development",
          description:
            "Build applications around your specific business processes, users, functionality, and integration requirements.",
          icon: CodeXml,
        },
        {
          title: "Backend Engineering",
          description:
            "Develop structured backend functionality for authentication, data management, business logic, APIs, and application workflows.",
          icon: ServerCog,
        },
        {
          title: "Database Expertise",
          description:
            "Design and integrate databases around application relationships, transactions, workflows, and reporting requirements.",
          icon: Database,
        },
        {
          title: "API & Integration Development",
          description:
            "Connect PHP applications with mobile apps, frontend platforms, payment gateways, and suitable third-party systems.",
          icon: Braces,
        },
        {
          title: "Application Modernization",
          description:
            "Improve suitable legacy PHP systems through code updates, architecture improvements, new functionality, and technology upgrades.",
          icon: RefreshCw,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support PHP applications with troubleshooting, upgrades, optimization, integrations, maintenance, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "PHP Solutions for Different Industries",
      description:
        "PHP web applications tailored to different business workflows, customer journeys, operational requirements, and digital platforms.",
      items: [
        {
          title: "Enterprise & Business",
          description:
            "Build internal applications, business portals, dashboards, workflow systems, and operational platforms.",
          icon: Building2,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Develop commerce functionality, customer systems, product workflows, order management, and backend integrations.",
          icon: ShoppingCart,
        },
        {
          title: "Education",
          description:
            "Create applications for students, courses, administration, communication, content, and institutional workflows.",
          icon: GraduationCap,
        },
        {
          title: "Healthcare",
          description:
            "Build appropriate applications for services, operational workflows, appointments, administration, and communication.",
          icon: HeartHandshake,
        },
        {
          title: "Real Estate",
          description:
            "Develop property platforms, lead management systems, dashboards, customer portals, and operational workflows.",
          icon: Building2,
        },
        {
          title: "Content & Publishing",
          description:
            "Create content-driven websites and management platforms for articles, media, users, publishing, and structured information.",
          icon: Newspaper,
        },
      ],
    },

    portfolio: {
      title: "PHP Development Success Stories",
      description:
        "PHP applications focused on custom business functionality, backend architecture, database workflows, integrations, and maintainability.",
      items: [
        {
          title: "Custom Business Portal",
          description:
            "Developed a PHP platform with user authentication, role-based access, operational workflows, dashboards, and centralized business data.",
          image: "/project-image.webp",
        },
        {
          title: "PHP Backend Platform",
          description:
            "Built a structured backend system with APIs, database operations, business logic, authentication, and third-party integrations.",
          image: "/project-image.webp",
        },
        {
          title: "Legacy PHP Modernization",
          description:
            "Modernized an existing PHP application with updated architecture, refactored functionality, improved integrations, and performance optimization.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire PHP Developers",
      description:
        "Partner with our PHP developers to build, modernize, integrate, optimize, and maintain reliable web applications and backend systems.",
      cta: "Let's Build Your PHP Application",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to develop and modernize PHP applications around practical operational requirements.",
      items: [
        {
          name: "Andrew Collins",
          company: "CoreLine Systems",
          location: "United Kingdom",
          review:
            "Our existing PHP platform had become difficult to maintain. Skand Tech Hub reorganized the backend and improved several important workflows without disrupting our day-to-day operations.",
        },
        {
          name: "Matthew Turner",
          company: "NexaWorks",
          location: "United States",
          review:
            "The team understood our custom backend requirements and built the APIs, database workflows, and admin functionality into a much cleaner application structure.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our PHP Development services.",
      items: [
        {
          question: "What PHP development services do you provide?",
          answer:
            "We provide custom PHP website and application development, backend development, API development, database integration, admin dashboards, third-party integrations, legacy modernization, testing, deployment, and ongoing technical support.",
        },
        {
          question: "Can PHP be used for custom web applications?",
          answer:
            "Yes. PHP can be used to build custom websites, business applications, portals, dashboards, APIs, content platforms, and other database-driven web systems.",
        },
        {
          question: "Can you work with an existing PHP application?",
          answer:
            "Yes. We can assess suitable existing PHP applications and provide troubleshooting, modernization, code improvements, new functionality, integrations, and performance optimization.",
        },
        {
          question: "Can you develop APIs using PHP?",
          answer:
            "Yes. We can build PHP-based APIs for frontend applications, mobile apps, external platforms, and third-party system integrations.",
        },
        {
          question: "Can PHP applications integrate with third-party services?",
          answer:
            "Yes. PHP applications can integrate with suitable payment gateways, communication services, external APIs, analytics tools, CRM platforms, and other business systems.",
        },
        {
          question: "Do you provide PHP maintenance and support?",
          answer:
            "Yes. We can provide PHP application troubleshooting, upgrades, security-related improvements, performance optimization, integration support, maintenance, and additional development.",
        },
      ],
    },
  },
  // ASP.NET Development
  {
    title: "ASP.NET Development",
    slug: "asp-net-development",
    group: "Website Development",
    icon: Building2,

    seo: {
      title: "ASP.NET Development Services",
      description:
        "Build secure, scalable, and enterprise-ready ASP.NET applications with robust architecture, business workflows, system integrations, and Microsoft technology expertise.",
      keywords: [
        "ASP.NET Development",
        "ASP.NET Development Services",
        "ASP.NET Core Development",
        ".NET Web Development",
        "ASP.NET Developers",
        "ASP.NET Development Company",
      ],
    },

    hero: {
      title: "ASP.NET Development for Enterprise Web Applications",
      description:
        "Develop dependable enterprise applications with .NET architecture and business system integrations.",
      highlights: [
        "Enterprise Authentication",
        "Azure-Ready Deployment",
        "Workflow Modules",
        "Reporting Systems",
      ],
    },

    whyChoose: {
      title: "Why Choose ASP.NET Development?",
      description:
        "ASP.NET provides a structured Microsoft technology foundation for building secure enterprise applications, business systems, APIs, portals, and integration-heavy web platforms.",
      items: [
        {
          title: "Enterprise-Ready Architecture",
          description:
            "Build structured applications designed around complex business workflows, organizational requirements, and long-term maintainability.",
          icon: Building2,
        },
        {
          title: "Secure Application Development",
          description:
            "Implement authentication, authorization, validation, permissions, and controlled access for business applications.",
          icon: ShieldCheck,
        },
        {
          title: "Microsoft Ecosystem Integration",
          description:
            "Connect applications with suitable Microsoft technologies, databases, Azure services, and enterprise systems.",
          icon: Boxes,
        },
        {
          title: "Scalable Business Systems",
          description:
            "Create application foundations capable of supporting growing users, modules, workflows, integrations, and business data.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our ASP.NET Development Services",
      description:
        "End-to-end ASP.NET development covering enterprise applications, APIs, business portals, dashboards, integrations, modernization, and cloud-ready solutions.",
      items: [
        {
          title: "Custom ASP.NET Development",
          description:
            "Build tailored ASP.NET applications around your business workflows, users, data, security, and operational requirements.",
          icon: Code2,
          features: [
            "Custom Web Applications",
            "Business Logic",
            "Role-Based Access",
            "Database Integration",
            "Enterprise Workflows",
          ],
        },
        {
          title: "ASP.NET Core API Development",
          description:
            "Develop structured backend APIs for web applications, mobile apps, enterprise platforms, and external system integrations.",
          icon: Braces,
          features: [
            "REST APIs",
            "API Authentication",
            "Business Services",
            "Third-Party Integration",
            "Data APIs",
          ],
        },
        {
          title: "Enterprise Portal & Dashboard Development",
          description:
            "Create centralized business platforms for managing users, workflows, operations, reporting, and organizational data.",
          icon: LayoutDashboard,
          features: [
            "Admin Portals",
            "Business Dashboards",
            "User Management",
            "Workflow Management",
            "Reporting Modules",
          ],
        },
        {
          title: "ASP.NET Modernization & Migration",
          description:
            "Modernize suitable existing .NET applications with updated architecture, ASP.NET Core, cloud-ready infrastructure, and improved functionality.",
          icon: RefreshCw,
          features: [
            ".NET Modernization",
            "ASP.NET Core Migration",
            "Architecture Improvements",
            "Feature Enhancement",
            "Azure Migration",
          ],
        },
      ],
    },

    features: {
      title: "Key ASP.NET Application Features",
      description:
        "Enterprise-focused capabilities for building secure, integrated, data-driven, and maintainable business applications.",
      items: [
        {
          title: "Enterprise Authentication",
          description:
            "Implement secure user authentication, authorization, roles, permissions, and controlled access across application modules.",
          icon: ShieldCheck,
        },
        {
          title: "Business Workflow Management",
          description:
            "Digitize structured organizational processes with approvals, statuses, assignments, permissions, and business rules.",
          icon: Workflow,
        },
        {
          title: "API Development",
          description:
            "Create structured APIs for frontend applications, mobile products, enterprise services, and external integrations.",
          icon: Braces,
        },
        {
          title: "SQL Server Integration",
          description:
            "Build data-driven applications around structured relational databases, business information, and reporting requirements.",
          icon: Database,
        },
        {
          title: "Reporting & Dashboards",
          description:
            "Provide business users with relevant operational reports, dashboards, metrics, and management information.",
          icon: BarChart3,
        },
        {
          title: "Azure-Ready Architecture",
          description:
            "Structure suitable applications for deployment and integration with Microsoft Azure infrastructure and services.",
          icon: Cloud,
        },
      ],
    },

    process: {
      title: "Our ASP.NET Development Process",
      description:
        "A structured enterprise development process focused on business requirements, application architecture, security, integrations, testing, and reliable deployment.",
      items: [
        {
          title: "Business Requirement Analysis",
          description:
            "Understand organizational workflows, users, security requirements, business data, integrations, reporting needs, and technical objectives.",
        },
        {
          title: "Architecture & Database Planning",
          description:
            "Define application modules, ASP.NET architecture, SQL Server structures, APIs, authentication, permissions, and system integrations.",
        },
        {
          title: "UI/UX & Workflow Design",
          description:
            "Design application interfaces, dashboards, forms, navigation, user journeys, and operational workflows.",
        },
        {
          title: "ASP.NET Development",
          description:
            "Develop application modules, APIs, business logic, authentication, database functionality, workflows, reports, and integrations.",
        },
        {
          title: "Testing & Quality Assurance",
          description:
            "Test functionality, permissions, APIs, workflows, integrations, database operations, security, compatibility, and performance.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the application to suitable infrastructure and provide ongoing maintenance, optimization, upgrades, and technical support.",
        },
      ],
    },

    benefits: {
      title: "Benefits of ASP.NET Development",
      description:
        "Build dependable enterprise applications with structured architecture, strong business integration capabilities, and support for evolving organizational requirements.",
      items: [
        {
          title: "Enterprise Application Reliability",
          description:
            "Create structured business platforms designed around organizational workflows, users, data, and operational requirements.",
          icon: Building2,
        },
        {
          title: "Secure Access Management",
          description:
            "Control application access through suitable authentication, roles, permissions, authorization, and validation workflows.",
          icon: ShieldCheck,
        },
        {
          title: "Microsoft Ecosystem Compatibility",
          description:
            "Integrate suitable applications with Microsoft technologies, SQL Server, Azure services, and existing enterprise systems.",
          icon: Boxes,
        },
        {
          title: "Complex Workflow Support",
          description:
            "Build structured business processes involving multiple users, departments, approvals, statuses, and operational rules.",
          icon: Workflow,
        },
        {
          title: "Data & Reporting Capabilities",
          description:
            "Manage structured business data and provide relevant reports, dashboards, and operational information.",
          icon: BarChart3,
        },
        {
          title: "Scalable Application Growth",
          description:
            "Extend application modules, users, integrations, workflows, and infrastructure as organizational requirements evolve.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "ASP.NET Development Technologies",
      description:
        "Microsoft technologies used to build enterprise-ready web applications, APIs, business systems, databases, and cloud deployments.",
      technologies: ["ASP.NET Core", "C#", "SQL Server", "Azure"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine enterprise application development, ASP.NET architecture, database engineering, business workflows, APIs, and system integrations to build dependable Microsoft-stack solutions.",
      items: [
        {
          title: "Enterprise-Focused Development",
          description:
            "Build applications around organizational processes, security requirements, users, data, and operational objectives.",
          icon: Building2,
        },
        {
          title: "ASP.NET Core Architecture",
          description:
            "Develop structured application foundations using ASP.NET Core and maintainable backend development practices.",
          icon: Code2,
        },
        {
          title: "Security & Access Control",
          description:
            "Implement suitable authentication, authorization, roles, permissions, validation, and application access workflows.",
          icon: ShieldCheck,
        },
        {
          title: "Business System Integration",
          description:
            "Connect applications with suitable enterprise services, databases, APIs, Microsoft technologies, and external platforms.",
          icon: Boxes,
        },
        {
          title: "Azure-Ready Development",
          description:
            "Structure suitable applications for Microsoft Azure deployment, infrastructure, and cloud service integrations.",
          icon: Cloud,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support ASP.NET applications with troubleshooting, upgrades, integrations, modernization, optimization, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "ASP.NET Solutions for Different Industries",
      description:
        "Enterprise web applications tailored to complex business workflows, organizational structures, data requirements, and system integrations.",
      items: [
        {
          title: "Enterprise & Corporate",
          description:
            "Build internal portals, workflow platforms, administrative systems, reporting applications, and enterprise dashboards.",
          icon: Building2,
        },
        {
          title: "Finance & Business Services",
          description:
            "Develop secure applications for structured workflows, data management, reporting, customer operations, and business processes.",
          icon: Landmark,
        },
        {
          title: "Healthcare",
          description:
            "Create appropriate enterprise applications for administration, service workflows, reporting, operations, and system integrations.",
          icon: HeartHandshake,
        },
        {
          title: "Education",
          description:
            "Build institutional platforms for students, administration, workflows, reporting, users, and organizational data.",
          icon: GraduationCap,
        },
        {
          title: "Manufacturing & Operations",
          description:
            "Develop business systems for operational workflows, users, reporting, internal processes, and enterprise data.",
          icon: Factory,
        },
        {
          title: "Government & Organizations",
          description:
            "Create structured web applications with controlled access, workflow management, reporting, and administrative functionality.",
          icon: Landmark,
        },
      ],
    },

    portfolio: {
      title: "ASP.NET Development Success Stories",
      description:
        "Enterprise applications focused on secure architecture, business workflows, reporting, integrations, and scalable Microsoft-stack development.",
      items: [
        {
          title: "Enterprise Operations Portal",
          description:
            "Developed an ASP.NET platform with enterprise authentication, role-based access, operational workflows, dashboards, reporting, and centralized business data.",
          image: "/project-image.webp",
        },
        {
          title: "Business Workflow Management System",
          description:
            "Built a structured application for managing multi-stage workflows, approvals, user permissions, operational records, and management reporting.",
          image: "/project-image.webp",
        },
        {
          title: "Enterprise API Platform",
          description:
            "Created ASP.NET Core APIs connecting business applications, SQL Server data, authentication services, and external system integrations.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire ASP.NET Developers",
      description:
        "Partner with our ASP.NET developers to build, integrate, modernize, deploy, and support secure enterprise web applications and business systems.",
      cta: "Let's Build Your ASP.NET Application",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to develop structured Microsoft-stack applications around complex enterprise requirements.",
      items: [
        {
          name: "Stephen Walker",
          company: "NorthBridge Systems",
          location: "United Kingdom",
          review:
            "Skand Tech Hub understood the complexity of our internal workflows and translated them into a much cleaner ASP.NET application. User permissions, reporting, and daily operations are now far easier to manage.",
        },
        {
          name: "Daniel Roberts",
          company: "CoreAxis Technologies",
          location: "United States",
          review:
            "The team handled our ASP.NET Core backend, SQL Server requirements, and business integrations very well. The application now gives us a reliable foundation for expanding our internal systems.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our ASP.NET Development services.",
      items: [
        {
          question: "What ASP.NET development services do you provide?",
          answer:
            "We provide custom ASP.NET application development, ASP.NET Core APIs, enterprise portals, dashboards, workflow systems, SQL Server integration, business system integrations, modernization, deployment, and ongoing technical support.",
        },
        {
          question: "Can ASP.NET be used for enterprise applications?",
          answer:
            "Yes. ASP.NET Core can be used to build enterprise web applications, APIs, portals, dashboards, workflow platforms, and other business systems requiring structured architecture and integration capabilities.",
        },
        {
          question: "Can you build APIs using ASP.NET Core?",
          answer:
            "Yes. We can develop ASP.NET Core APIs for web applications, mobile apps, enterprise systems, frontend platforms, and suitable third-party integrations.",
        },
        {
          question: "Can ASP.NET applications integrate with SQL Server?",
          answer:
            "Yes. ASP.NET applications can integrate with SQL Server for structured business data, relational workflows, reporting, transactions, and application operations.",
        },
        {
          question: "Can you deploy ASP.NET applications on Azure?",
          answer:
            "Yes. Suitable ASP.NET applications can be prepared for deployment and integration with Microsoft Azure infrastructure and services according to project requirements.",
        },
        {
          question: "Do you provide ASP.NET maintenance and modernization?",
          answer:
            "Yes. We can provide troubleshooting, ASP.NET Core migration, application upgrades, architecture improvements, integration support, performance optimization, and additional development.",
        },
      ],
    },
  },
  // Website maintenance
  {
    title: "Website Maintenance & Support",
    slug: "website-maintenance-support",
    group: "Website Development",
    icon: Wrench,

    seo: {
      title: "Website Maintenance & Support Services",
      description:
        "Keep your website secure, updated, optimized, and reliable with professional website maintenance, monitoring, troubleshooting, performance optimization, and ongoing technical support.",
      keywords: [
        "Website Maintenance",
        "Website Maintenance Services",
        "Website Support Services",
        "Website Maintenance and Support",
        "Website Technical Support",
        "Website Maintenance Company",
      ],
    },

    hero: {
      title: "Website Maintenance & Support for Reliable Digital Experiences",
      description:
        "Keep your website secure, stable, updated, and performing smoothly with ongoing technical maintenance and professional support.",
      highlights: [
        "Regular Maintenance",
        "Security Updates",
        "Performance Optimization",
        "Technical Support",
      ],
    },

    whyChoose: {
      title: "Why Invest in Website Maintenance & Support?",
      description:
        "Ongoing website maintenance helps businesses keep digital platforms secure, functional, compatible, and optimized as technologies, content, integrations, and business requirements evolve.",
      items: [
        {
          title: "Reliable Website Performance",
          description:
            "Identify and resolve technical issues that can affect website availability, functionality, responsiveness, and user experience.",
          icon: Gauge,
        },
        {
          title: "Security Maintenance",
          description:
            "Keep relevant website software, dependencies, plugins, and configurations updated to reduce avoidable security risks.",
          icon: ShieldCheck,
        },
        {
          title: "Continuous Improvements",
          description:
            "Improve website functionality, content, integrations, performance, and user experiences as requirements change.",
          icon: TrendingUp,
        },
        {
          title: "Technical Assistance",
          description:
            "Get development support when website errors, integration problems, deployment issues, or unexpected technical requirements arise.",
          icon: Headphones,
        },
      ],
    },

    services: {
      title: "Our Website Maintenance & Support Services",
      description:
        "Ongoing technical support covering website updates, troubleshooting, security maintenance, performance optimization, integrations, and continuous improvements.",
      items: [
        {
          title: "Website Maintenance",
          description:
            "Keep website software, functionality, content, integrations, and technical components properly maintained.",
          icon: Wrench,
          features: [
            "Software Updates",
            "Dependency Updates",
            "Plugin Updates",
            "Content Updates",
            "Technical Maintenance",
          ],
        },
        {
          title: "Website Troubleshooting",
          description:
            "Diagnose and resolve website errors, broken functionality, layout issues, integration problems, and unexpected behaviour.",
          icon: Bug,
          features: [
            "Error Resolution",
            "Bug Fixing",
            "Broken Functionality",
            "Integration Issues",
            "Compatibility Fixes",
          ],
        },
        {
          title: "Performance Optimization",
          description:
            "Improve suitable website performance through frontend, asset, caching, database, and application-level optimization.",
          icon: Gauge,
          features: [
            "Speed Optimization",
            "Asset Optimization",
            "Caching Improvements",
            "Database Optimization",
            "Performance Audits",
          ],
        },
        {
          title: "Website Enhancement & Support",
          description:
            "Extend existing websites with new functionality, integrations, pages, workflows, and technical improvements.",
          icon: Settings,
          features: [
            "Feature Enhancements",
            "New Pages",
            "API Integrations",
            "UI Improvements",
            "Technical Support",
          ],
        },
      ],
    },

    features: {
      title: "Key Website Maintenance Capabilities",
      description:
        "Practical maintenance capabilities for keeping websites updated, stable, secure, optimized, and aligned with evolving business requirements.",
      items: [
        {
          title: "Software Updates",
          description:
            "Maintain relevant frameworks, CMS platforms, plugins, packages, and dependencies according to website requirements.",
          icon: RefreshCw,
        },
        {
          title: "Security Updates",
          description:
            "Apply suitable security-related updates and maintenance across website software and technical components.",
          icon: ShieldCheck,
        },
        {
          title: "Bug Fixing",
          description:
            "Identify and resolve functionality, layout, integration, compatibility, and application issues.",
          icon: Bug,
        },
        {
          title: "Performance Monitoring",
          description:
            "Review relevant website performance indicators and investigate technical issues affecting user experiences.",
          icon: Activity,
        },
        {
          title: "Backup Support",
          description:
            "Maintain suitable website and database backup workflows according to hosting and application requirements.",
          icon: DatabaseBackup,
        },
        {
          title: "Technical Improvements",
          description:
            "Implement suitable code, architecture, integration, UI, and functionality improvements over time.",
          icon: Settings,
        },
      ],
    },

    process: {
      title: "Our Website Maintenance Process",
      description:
        "A structured support process focused on understanding the existing platform, identifying priorities, resolving technical issues, and maintaining website reliability.",
      items: [
        {
          title: "Website Assessment",
          description:
            "Review the existing website, technology stack, hosting environment, integrations, functionality, and current technical concerns.",
        },
        {
          title: "Maintenance Planning",
          description:
            "Identify updates, technical risks, performance issues, recurring requirements, and maintenance priorities.",
        },
        {
          title: "Updates & Maintenance",
          description:
            "Apply suitable software, dependency, plugin, content, configuration, and technical updates.",
        },
        {
          title: "Issue Resolution",
          description:
            "Investigate and resolve bugs, broken functionality, integration problems, compatibility issues, and technical errors.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Test relevant website functionality after changes and implement suitable performance and reliability improvements.",
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Continue supporting the website with maintenance, troubleshooting, enhancements, integrations, and future technical requirements.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Website Maintenance & Support",
      description:
        "Protect the long-term value of your website by keeping its technology, functionality, performance, and user experience properly maintained.",
      items: [
        {
          title: "Improved Reliability",
          description:
            "Reduce avoidable website issues through regular maintenance, testing, troubleshooting, and technical improvements.",
          icon: ShieldCheck,
        },
        {
          title: "Better Performance",
          description:
            "Identify and improve suitable technical factors affecting website speed, responsiveness, and user experiences.",
          icon: Gauge,
        },
        {
          title: "Reduced Technical Risk",
          description:
            "Keep relevant website technologies and dependencies updated instead of allowing outdated components to accumulate.",
          icon: TriangleAlert,
        },
        {
          title: "Faster Issue Resolution",
          description:
            "Get technical assistance when website bugs, errors, integrations, or unexpected problems require developer attention.",
          icon: Wrench,
        },
        {
          title: "Continuous Website Improvement",
          description:
            "Enhance functionality, pages, integrations, content experiences, and technical foundations as business requirements evolve.",
          icon: TrendingUp,
        },
        {
          title: "Long-Term Website Stability",
          description:
            "Maintain a healthier technical foundation through ongoing updates, monitoring, testing, and support.",
          icon: Activity,
        },
      ],
    },

    techStack: {
      title: "Website Maintenance Technologies",
      description:
        "Support for modern web technologies, content management platforms, hosting environments, and website infrastructure.",
      technologies: ["React", "Next.js", "WordPress", "PHP", "Nginx"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We provide development-focused website maintenance that goes beyond routine updates to include troubleshooting, optimization, integrations, and continuous technical improvements.",
      items: [
        {
          title: "Development Expertise",
          description:
            "Troubleshoot website issues at the code, frontend, backend, database, integration, and infrastructure levels.",
          icon: Code2,
        },
        {
          title: "Proactive Maintenance",
          description:
            "Address relevant updates, technical issues, and improvement opportunities before they become larger website problems.",
          icon: Wrench,
        },
        {
          title: "Performance Focus",
          description:
            "Investigate and optimize suitable website components affecting loading speed, responsiveness, and technical performance.",
          icon: Gauge,
        },
        {
          title: "Security-Conscious Support",
          description:
            "Maintain relevant software, dependencies, access, configurations, and technical components with website security in mind.",
          icon: ShieldCheck,
        },
        {
          title: "Flexible Technical Support",
          description:
            "Support ongoing fixes, enhancements, integrations, content requirements, and development changes as needed.",
          icon: Settings,
        },
        {
          title: "Long-Term Partnership",
          description:
            "Provide continued technical assistance as your website, technology, integrations, and business requirements evolve.",
          icon: Handshake,
        },
      ],
    },

    industrySolutions: {
      title: "Website Maintenance for Different Businesses",
      description:
        "Ongoing website support tailored to different platforms, operational requirements, content needs, and digital business models.",
      items: [
        {
          title: "Corporate Websites",
          description:
            "Maintain service pages, integrations, forms, content, website functionality, and technical performance for corporate platforms.",
          icon: Building2,
        },
        {
          title: "E-Commerce Websites",
          description:
            "Support storefront functionality, payments, checkout, products, integrations, updates, and commerce-related technical issues.",
          icon: ShoppingCart,
        },
        {
          title: "WordPress Websites",
          description:
            "Maintain WordPress core, themes, plugins, content, integrations, performance, and custom functionality.",
          icon: PanelsTopLeft,
        },
        {
          title: "Web Applications",
          description:
            "Support application functionality, APIs, authentication, dashboards, databases, dependencies, and frontend experiences.",
          icon: AppWindow,
        },
        {
          title: "Content Platforms",
          description:
            "Maintain publishing systems, CMS functionality, templates, media, integrations, and content-related technical workflows.",
          icon: Newspaper,
        },
        {
          title: "SaaS Platforms",
          description:
            "Provide ongoing technical support for web-based products, dashboards, user workflows, integrations, and application functionality.",
          icon: Cloud,
        },
      ],
    },

    portfolio: {
      title: "Website Maintenance & Support Success Stories",
      description:
        "Ongoing website support focused on stability, performance, troubleshooting, modernization, and continuous technical improvement.",
      items: [
        {
          title: "Corporate Website Maintenance",
          description:
            "Provided ongoing technical maintenance covering website updates, bug fixes, content changes, performance improvements, and new functionality.",
          image: "/project-image.webp",
        },
        {
          title: "E-Commerce Support & Optimization",
          description:
            "Supported an online store with checkout troubleshooting, integration fixes, performance improvements, platform updates, and ongoing enhancements.",
          image: "/project-image.webp",
        },
        {
          title: "Legacy Website Modernization",
          description:
            "Improved an aging website through dependency updates, code fixes, responsive improvements, performance optimization, and technical modernization.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Website Maintenance Experts",
      description:
        "Partner with our developers for ongoing website maintenance, troubleshooting, performance optimization, updates, enhancements, and technical support.",
      cta: "Get Website Support",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to keep their websites reliable, updated, and technically supported.",
      items: [
        {
          name: "Christopher Hall",
          company: "WestBridge Consulting",
          location: "United Kingdom",
          review:
            "Skand Tech Hub has been handling our website maintenance and technical changes consistently. Issues are addressed quickly and we no longer have to worry about routine updates or unexpected website problems.",
        },
        {
          name: "Brian Foster",
          company: "NexaRetail",
          location: "United States",
          review:
            "The team helped stabilize our website after several recurring issues and has continued supporting us with performance improvements, updates, and new functionality.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Website Maintenance & Support services.",
      items: [
        {
          question: "What is included in website maintenance?",
          answer:
            "Website maintenance can include suitable software and dependency updates, bug fixes, troubleshooting, content changes, performance optimization, security-related maintenance, backups, integrations, testing, and technical improvements.",
        },
        {
          question: "Can you maintain a website developed by another company?",
          answer:
            "Yes. We can first review the existing codebase, technology stack, hosting environment, documentation, and overall condition before determining the appropriate maintenance approach.",
        },
        {
          question: "Do you support WordPress websites?",
          answer:
            "Yes. We can support suitable WordPress websites with core, theme and plugin updates, troubleshooting, custom functionality, performance improvements, integrations, and other technical maintenance.",
        },
        {
          question: "Can you maintain React or Next.js websites?",
          answer:
            "Yes. We can provide maintenance and development support for suitable React and Next.js websites, including dependency updates, bug fixing, frontend improvements, integrations, performance work, and deployment-related support.",
        },
        {
          question: "Can you add new features while maintaining our website?",
          answer:
            "Yes. Website maintenance can also include suitable feature enhancements, new pages, integrations, UI improvements, workflow changes, and other development requirements.",
        },
        {
          question: "Do you provide ongoing website support?",
          answer:
            "Yes. We can provide ongoing technical support based on your website's technology, maintenance requirements, development needs, and agreed support scope.",
        },
      ],
    },
  },
];
